<script setup>
import KpiIndicatorCard from '@/views/quality/kpi-report/KpiIndicatorCard.vue'
import KpiTrendDialog from '@/views/quality/kpi-report/KpiTrendDialog.vue'
import RiskPromoteDialog from '@/views/quality/risk/RiskPromoteDialog.vue'

definePage({
  meta: {
    name: 'incident-kpi-report',
    subject: 'quality.menu',
    action: 'kpi_report',
  },
})

// ─── Filters ───

const selectedFramework = ref(null)
const selectedChapter = ref(null)
const selectedStatus = ref('all')
const search = ref('')
const debouncedSearch = refDebounced(search, 500)

const statusOptions = [
  { title: 'All', value: 'all' },
  { title: 'Normal', value: 'NORMAL' },
  { title: 'Warning', value: 'WARNING' },
  { title: 'Critical', value: 'CRITICAL' },
  { title: 'Incomplete', value: 'INCOMPLETE' },
  { title: 'Skipped', value: 'SKIPPED' },
]

// Reset chapter when framework changes
watch(selectedFramework, () => {
  selectedChapter.value = null
})

// ─── Framework & Chapter Dropdowns ───

const frameworks = ref([])

useApi(createUrl('/masters/tags', {
  query: {
    itemsPerPage: 50,
    select: ['id', 'name'],
    search: { tag_type: 'KPI_FRAMEWORK' },
  },
}), {
  afterFetch: ({ data }) => {
    frameworks.value = data.data ?? data ?? []
  },
})

const chapters = ref([])

useApi(createUrl('/masters/tags', {
  query: {
    itemsPerPage: 50,
    select: ['id', 'name'],
    search: { tag_type: 'KPI_CHAPTER' },
  },
}), {
  afterFetch: ({ data }) => {
    chapters.value = data.data ?? data ?? []
  },
})

// ─── Fetch Evaluations ───

const filters = computed(() => {
  const base = {}

  if (selectedFramework.value) {
    base['configuration.framework_id'] = selectedFramework.value
  }
  if (selectedChapter.value) {
    base['configuration.chapter_id'] = selectedChapter.value
  }
  if (selectedStatus.value !== 'all') {
    base.status = [selectedStatus.value]
  }

  return base
})

const {
  isFetching,
  data: evaluationsData,
} = useApi(createUrl('/incident/kpi-evaluations', {
  query: {
    q: debouncedSearch,
    search: filters,
    with: ['configuration.framework', 'configuration.chapter', 'rca'],
  },
}))

// Deduplicate: keep only the latest evaluation per configuration
const evaluations = computed(() => {
  const items = evaluationsData.value?.data ?? evaluationsData.value ?? []
  const seen = new Map()

  for (const item of items) {
    const existing = seen.get(item.kpi_configuration_id)

    if (!existing || item.period_start > existing.period_start) {
      seen.set(item.kpi_configuration_id, item)
    }
  }

  return [...seen.values()]
})

// ─── Trend Dialog ───

const isTrendDialogOpen = ref(false)
const trendEvaluation = ref(null)

const openTrendDialog = evaluation => {
  trendEvaluation.value = evaluation
  isTrendDialogOpen.value = true
}

// ─── Promote to Risk ───

const router = useRouter()

const { isOpen: isPromoteDialogOpen, source: promoteSource, promptPromote } = useRiskPromotion()

const openPromoteDialog = ({ id, unitId, prefill }) => {
  promptPromote('KPI_EVALUATION', id, unitId, prefill)
}

const onPromoted = risk => {
  router.push({ name: 'app-quality-risk-id', params: { id: risk.id } })
}
</script>

<template>
  <section>
    <!-- Filters -->
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText class="d-flex flex-wrap gap-4">
        <div style="min-inline-size: 12rem;">
          <VSelect
            v-model="selectedFramework"
            :items="frameworks"
            item-title="name"
            item-value="id"
            placeholder="Framework"
            density="compact"
            clearable
            hide-details
          />
        </div>

        <div style="min-inline-size: 12rem;">
          <VSelect
            v-model="selectedChapter"
            :items="chapters"
            item-title="name"
            item-value="id"
            placeholder="Chapter"
            density="compact"
            clearable
            hide-details
          />
        </div>

        <div style="min-inline-size: 10rem;">
          <VSelect
            v-model="selectedStatus"
            :items="statusOptions"
            item-title="title"
            item-value="value"
            density="compact"
            hide-details
          />
        </div>

        <VSpacer />

        <div style="min-inline-size: 10rem; max-inline-size: 12rem;">
          <AppTextField
            v-model="search"
            placeholder="Search"
            density="compact"
            hide-details
          />
        </div>
      </VCardText>
    </VCard>

    <!-- Loading State -->
    <template v-if="isFetching">
      <VRow>
        <VCol
          v-for="n in 4"
          :key="n"
          cols="12"
          md="6"
        >
          <VSkeletonLoader
            type="card"
            height="280"
          />
        </VCol>
      </VRow>
    </template>

    <!-- Empty State -->
    <template v-else-if="evaluations.length === 0">
      <VCard
        variant="flat"
        class="border"
      >
        <VCardText class="text-center pa-8">
          <VIcon
            icon="tabler-chart-dots-3"
            size="48"
            class="text-medium-emphasis mb-2"
          />
          <div class="text-body-1 text-medium-emphasis">
            No KPI evaluations found.
          </div>
        </VCardText>
      </VCard>
    </template>

    <!-- Indicator Cards Grid -->
    <VRow v-else>
      <VCol
        v-for="evaluation in evaluations"
        :key="evaluation.id"
        cols="12"
        md="6"
      >
        <KpiIndicatorCard
          :evaluation="evaluation"
          @open-trend="openTrendDialog"
          @promote="openPromoteDialog"
        />
      </VCol>
    </VRow>

    <!-- Trend Dialog -->
    <KpiTrendDialog
      v-model:is-dialog-open="isTrendDialogOpen"
      :evaluation="trendEvaluation"
    />

    <!-- Promote to Risk -->
    <RiskPromoteDialog
      v-model:is-dialog-open="isPromoteDialogOpen"
      :source-type="promoteSource.type"
      :source-id="promoteSource.id"
      :source-unit-id="promoteSource.unitId"
      :prefill="promoteSource.prefill"
      @promoted="onPromoted"
    />
  </section>
</template>
